import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { atom, useAtom } from 'jotai';
import { isEmpty, orderBy } from 'lodash';
import { useEffect } from 'react';

import PERMISSIONS from '@/constants/auth.constants';
import type { SearchFilters } from '@/features/SearchBar';
import useHandleError from '@/hooks/useHandleError';
import { axiosClient } from '@/services/BaseApi.service';
import { checkPermissions } from '@/services/permissions';
import { userAtom } from '@/store/user.store';

import { selectedOrganizationAtom } from '../Organizations';
import type { ReportsCount, ReportType } from './Reports.types';

export const reportsFiltersAtom = atom<SearchFilters>({});

export const reportsAtom = atom<ReportType[]>([]);
export const allReportsAtom = atom<ReportType[]>([]);

export const useFetchAllReports = () => {
  const [, setAllReports] = useAtom(allReportsAtom);
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const handleError = useHandleError();

  const { data, refetch, isSuccess, isError, error } = useQuery({
    queryKey: [`reports/`],
    enabled: selectedOrganization.label === 'all',
    staleTime: 60 * 10 * 1000 // 10 minutes
  });

  useEffect(() => {
    if (isError) {
      handleError(error as AxiosError, 'Failure to fetch all reports.');
    }
    if (isSuccess) {
      const formattedData = (data as ReportType[]).map((report: ReportType) => {
        let report_frequency_name = '';
        switch (report.report_frequency_id) {
          case 1:
            report_frequency_name = 'Daily';
            break;
          case 2:
            report_frequency_name = 'Weekly';
            break;
          case 3:
            report_frequency_name = 'Monthly';
            break;
          default:
            report_frequency_name = 'Custom';
            break;
        }
        return {
          ...report,
          report_frequency_name
        };
      });
      setAllReports(orderBy(formattedData, 'created_at', 'desc'));
    }
  }, [data, isError, isSuccess, error]);

  return { refetch };
};

const prevReports: ReportType[] = [];
export const useFetchReports = () => {
  const [, setReports] = useAtom(reportsAtom);
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const handleError = useHandleError();
  const [userData] = useAtom(userAtom);

  const { data, refetch, isError, error } = useQuery({
    queryKey: [`organizations/${selectedOrganization.label}/reports`],
    staleTime: 60 * 10 * 1000, // 10 minutes
    enabled:
      selectedOrganization.label !== 'all' &&
      checkPermissions(
        [PERMISSIONS.REPORT_READ],
        userData,
        selectedOrganization
      )
  });

  useEffect(() => {
    if (isError) {
      handleError(error as AxiosError, 'Failure to fetch reports.');
    }
    if (data && data !== prevReports) {
      const formattedData = (data as ReportType[]).map((report: ReportType) => {
        let report_frequency_name = '';
        switch (report.report_frequency_id) {
          case 1:
            report_frequency_name = 'Daily';
            break;
          case 2:
            report_frequency_name = 'Weekly';
            break;
          case 3:
            report_frequency_name = 'Monthly';
            break;
          default:
            report_frequency_name = 'Custom';
            break;
        }
        return {
          ...report,
          report_frequency_name
        };
      });

      setReports(orderBy(formattedData, 'created_at', 'desc'));
    }
  }, [data, isError]);

  return { refetch };
};

export const reportsCountsAtom = atom<ReportsCount>({
  all: {},
  organizations: {}
});

export const newReportsCountAtom = atom<number>((get) => {
  const user = get(userAtom);
  const reportsCounts = get(reportsCountsAtom);
  const selectedOrganization = get(selectedOrganizationAtom);

  const orgCounts =
    selectedOrganization.label === 'all'
      ? reportsCounts.all
      : reportsCounts.organizations[selectedOrganization.label] || {};
  let total = 0;

  Object.entries(orgCounts).forEach(([typeIdStr, count]) => {
    const typeId = parseInt(typeIdStr, 10);
    let hasPermission = false;

    switch (typeId) {
      case 1: // MDR Reports
        hasPermission = checkPermissions(
          [PERMISSIONS.MDR_READ],
          user,
          selectedOrganization
        );
        break;
      case 2: // Executive Reports
        hasPermission = checkPermissions(
          [PERMISSIONS.MDR_READ],
          user,
          selectedOrganization
        );
        break;
      case 3: // VMP/Vulnerability Reports
        hasPermission = checkPermissions(
          [PERMISSIONS.VMP_READ],
          user,
          selectedOrganization
        );
        break;
      case 4: // EDR Reports
        hasPermission = checkPermissions(
          [PERMISSIONS.MDR_READ],
          user,
          selectedOrganization
        );
        break;
      default:
        hasPermission = false;
    }

    if (hasPermission) {
      total += count;
    }
  });

  return total;
});

export const filteredReportsAtom = atom<ReportType[]>((get) => {
  const reports = get(reportsAtom);
  const allReports = get(allReportsAtom);
  const selectedOrganization = get(selectedOrganizationAtom);
  const filters = get(reportsFiltersAtom);

  const selectedReports =
    selectedOrganization.label === 'all' ? allReports : reports;

  if (isEmpty(filters)) return selectedReports;

  return selectedReports.filter((report) =>
    Object.keys(filters).every((k) =>
      filters[k].some((f) => {
        if (k === 'status') {
          return report.report_frequency_name === f;
        }

        return report.report_title?.toLowerCase().includes(f.toLowerCase());
      })
    )
  );
});

export const mdrReportsAtom = atom<ReportType[]>((get) => {
  const filteredReports = get(filteredReportsAtom);
  return filteredReports.filter(
    (report: ReportType) => report.report_type_id === 1
  );
});

export const executiveReportsAtom = atom<ReportType[]>((get) => {
  const filteredReports = get(filteredReportsAtom);
  return filteredReports.filter(
    (report: ReportType) => report.report_type_id === 2
  );
});

export const vmpReportsAtom = atom<ReportType[]>((get) => {
  const filteredReports = get(filteredReportsAtom);
  return filteredReports.filter(
    (report: ReportType) => report.report_type_id === 3
  );
});

export const edrReportsAtom = atom<ReportType[]>((get) => {
  const filteredReports = get(filteredReportsAtom);
  return filteredReports.filter(
    (report: ReportType) => report.report_type_id === 4
  );
});

export const useCreateReport = (
  refetch: () => void,
  refetchAll: () => void
) => {
  const queryClient = useQueryClient();
  const handleError = useHandleError();
  return useMutation({
    mutationFn: (reportData: ReportType) => {
      const newReport = { ...reportData };
      const newFile = newReport.attachments[0];
      newReport.report_file = newFile;
      return axiosClient.post(`reports/`, newReport, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Accept: 'application/json'
        }
      });
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to fetch create report.');
    },
    onSuccess: () => {
      refetch();
      refetchAll();
      queryClient.invalidateQueries({ queryKey: ['reports/count'] });
    }
  });
};

export const useUpdateReport = (
  refetch: () => void,
  refetchAll: () => void
) => {
  const queryClient = useQueryClient();
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const handleError = useHandleError();

  return useMutation({
    mutationFn: (data: { new_report_name: string; report_id: number }) => {
      const newName = data.new_report_name;
      return axiosClient.put(
        `/reports/update_name/${data.report_id}?new_report_name=${newName}`
      );
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to update report.');
    },
    onSuccess: () => {
      if (selectedOrganization.label !== 'all') {
        refetch();
      } else {
        refetchAll();
      }
      queryClient.invalidateQueries({ queryKey: ['reports/count'] });
    }
  });
};

export const useDeleteReport = (
  refetch: () => void,
  refetchAll: () => void
) => {
  const queryClient = useQueryClient();
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const handleError = useHandleError();

  return useMutation({
    mutationFn: (id: number) => {
      return axiosClient.delete(`reports/${id}`);
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to fetch delete report.');
    },
    onSuccess: () => {
      if (selectedOrganization.label !== 'all') {
        refetch();
      } else {
        refetchAll();
      }
      queryClient.invalidateQueries({ queryKey: ['reports/count'] });
    }
  });
};

export const useFetchNewReportsCount = () => {
  const [, setReportsCounts] = useAtom(reportsCountsAtom);
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const [userData] = useAtom(userAtom);
  const handleError = useHandleError();

  const { data, refetch, isSuccess, isError, error } = useQuery({
    queryKey: [`reports/count`],
    staleTime: 60 * 10 * 1000, // 10 minutes
    enabled: checkPermissions(
      [PERMISSIONS.REPORT_READ],
      userData,
      selectedOrganization
    )
  });

  useEffect(() => {
    if (isError) {
      handleError(error as AxiosError, 'Failure to fetch new reports count.');
    }
    if (isSuccess && data) {
      setReportsCounts(data as ReportsCount);
    }
  }, [data, isError, isSuccess, error]);

  return { refetch };
};
